import * as React from 'react'
import { Eye, EyeOff } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Input, type InputProps } from './input'

export interface PasswordInputProps extends Omit<InputProps, 'type'> {
  /** Noun used in the reveal toggle's aria-label, e.g. "current
   * password". Defaults to "password". Set it when a screen has more
   * than one password field so screen-reader users can tell the
   * toggles apart. */
  revealLabel?: string
  /** When true, the field re-masks automatically once focus moves
   * outside the input + its own reveal toggle (#1206). Off by default
   * so existing callers (sign-in, sign-up, change-password) keep the
   * behavior they ship today; opted into by the credential editor
   * under Test Data, where a revealed secret left on screen if the
   * operator walks away is the failure mode we want to prevent.
   * Focus moving between the input and the toggle does NOT re-mask —
   * clicking the toggle would otherwise immediately undo itself. */
  reMaskOnBlur?: boolean
}

/**
 * Password text input with a built-in show/hide reveal toggle.
 *
 * NIST SP 800-63B recommends letting users reveal the secret they're
 * typing — it catches typos without a second "confirm password"
 * field. This is the single component every password field in the app
 * uses, so the affordance is consistent everywhere (sign-in, sign-up,
 * reset, change-password).
 *
 * `show` is internal state — revealing is purely presentational and
 * no parent needs to drive it. To force a re-mask (e.g. after a
 * successful password change has cleared the field), remount the
 * component by giving it a `key` that changes on success.
 *
 * All standard input attributes (`value`, `onChange`, `autoComplete`,
 * `required`, `minLength`, `maxLength`, `disabled`, `aria-*`, …) pass
 * straight through to the underlying `Input`. `type` is owned by this
 * component and therefore omitted from the props.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, revealLabel = 'password', reMaskOnBlur, onBlur, ...props }, ref) => {
    const [show, setShow] = React.useState(false)
    const wrapperRef = React.useRef<HTMLDivElement>(null)

    // Re-mask when focus actually leaves the input + toggle pair, not
    // when focus moves between them. `relatedTarget` is the element
    // gaining focus; if it's still inside the wrapper, the operator is
    // just bouncing between the field and its own toggle and we
    // shouldn't undo their reveal.
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (reMaskOnBlur && show) {
        const next = e.relatedTarget as Node | null
        if (!next || !wrapperRef.current?.contains(next)) {
          setShow(false)
        }
      }
      onBlur?.(e)
    }

    // Mirror the same logic on the toggle button so click-toggle →
    // tab-away also re-masks. Without this, clicking the eye to
    // reveal, then tabbing to the next field, would leave the
    // password visible because the input's blur already fired before
    // `show` flipped.
    const handleToggleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
      if (reMaskOnBlur && show) {
        const next = e.relatedTarget as Node | null
        if (!next || !wrapperRef.current?.contains(next)) {
          setShow(false)
        }
      }
    }

    const toggleLabel = show ? `Hide ${revealLabel}` : `Show ${revealLabel}`

    return (
      <div ref={wrapperRef} className="relative">
        <Input
          ref={ref}
          type={show ? 'text' : 'password'}
          disabled={disabled}
          // pr-9 leaves room for the toggle button so long values
          // don't slide under it.
          className={cn('pr-9', className)}
          {...props}
          onBlur={handleBlur}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          onBlur={handleToggleBlur}
          disabled={disabled}
          aria-label={toggleLabel}
          title={toggleLabel}
          aria-pressed={show}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'
